import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import StripedDataGrid from 'shared/components/StripedDataGrid';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import { CoverageResponse, LossesResponse, LossRow } from './types';
import { auctionInsightsUrl } from './analysis';
import UploadDialog from './components/UploadDialog';
import AlertSettingsDialog from './components/AlertSettingsDialog';
import CompetitorPanel from './components/CompetitorPanel';
import MoversTab from './components/MoversTab';
import CampaignHealthTab from './components/CampaignHealthTab';

// Auction Intelligence: where are we losing Google Ads impression share, is it a
// budget or a rank problem, and (from uploaded auction-insights exports) who is
// taking it. Backed by /auction-intelligence on the API; competitor share rows
// come from Google Ads UI CSV uploads because the API allowlist for auction
// insights is closed.

const service = new AuctionIntelligenceService();

const pct = (v: number | null | undefined): string =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;

const money = (v: number | null | undefined): string =>
  v == null ? '—' : `$${Math.round(v).toLocaleString()}`;

// Opens the Google Ads Auction insights page for the row's entity, scoped to
// the same trailing window the dashboard is comparing.
function googleAdsUrl(row: LossRow, days: number): string {
  const end = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  return auctionInsightsUrl(String(row.account_id), String(row.campaign_id), {
    adGroupId: row.ad_group_id != null ? String(row.ad_group_id) : undefined,
    start,
    end,
  });
}

interface SelectedEntity {
  row: LossRow;
}

export default function AuctionIntelligence() {
  const [level, setLevel] = useState<'ad_group' | 'campaign'>('ad_group');
  const [days, setDays] = useState(7);
  const [minSpend, setMinSpend] = useState(100);
  const [losses, setLosses] = useState<LossesResponse | null>(null);
  const [coverage, setCoverage] = useState<CoverageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [alertSettingsSaved, setAlertSettingsSaved] = useState(false);
  const [selected, setSelected] = useState<SelectedEntity | null>(null);
  // Campaign health first: the campaign manager's home base — which of my
  // campaigns are losing impression share, to whom, and how bad. The other
  // tabs are the account-wide and ad-group-level lenses behind it.
  const [tab, setTab] = useState(0);

  const refreshCoverage = useCallback(() => {
    service.getCoverage().then(setCoverage);
  }, []);

  const refreshLosses = useCallback(() => {
    setLoading(true);
    service
      .getLosses({ level, days, minSpend })
      .then(setLosses)
      .finally(() => setLoading(false));
  }, [level, days, minSpend]);

  useEffect(refreshCoverage, [refreshCoverage]);
  useEffect(refreshLosses, [refreshLosses]);

  const columns = useMemo<GridColDef[]>(() => {
    const base: GridColDef[] = [
      {
        field: 'store',
        headerName: 'Store',
        width: 80,
        renderCell: (p: GridRenderCellParams) => (
          <Chip size="small" label={String(p.value)} />
        ),
      },
      { field: 'campaign_name', headerName: 'Campaign', flex: 1.4, minWidth: 240 },
    ];
    if (level === 'ad_group') {
      base.push({
        field: 'ad_group_name',
        headerName: 'Ad group',
        flex: 1,
        minWidth: 180,
      });
    }
    base.push(
      {
        field: 'spend',
        headerName: `Spend ${days}d`,
        width: 100,
        type: 'number',
        valueFormatter: ({ value }) => money(Number(value)),
      },
      {
        field: 'current_share',
        headerName: 'IS now',
        width: 90,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        field: 'baseline_share',
        headerName: 'IS baseline',
        width: 100,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        field: 'drop_pp',
        headerName: 'Δ share',
        width: 90,
        type: 'number',
        renderCell: (p: GridRenderCellParams) => {
          // drop_pp is "how much share we LOST" (positive = loss). Render as a
          // signed delta: a negative drop is a GAIN and shows as +X.Xpp green —
          // never "--X.Xpp".
          const delta = -Number(p.value);
          return (
            <span
              style={{
                color:
                  delta <= -10 ? '#c62828' : delta <= -5 ? '#e65100' : delta > 0 ? '#2e7d32' : undefined,
                fontWeight: Math.abs(delta) >= 10 ? 700 : 400,
              }}
            >
              {delta > 0 ? '+' : ''}
              {delta.toFixed(1)}pp
            </span>
          );
        },
      },
      {
        field: 'campaign_budget_lost',
        headerName: 'Lost: budget',
        width: 105,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        field: 'campaign_rank_lost',
        headerName: 'Lost: rank',
        width: 95,
        type: 'number',
        valueFormatter: ({ value }) => pct(value == null ? null : Number(value)),
      },
      {
        field: 'estimated_weekly_at_risk',
        headerName: '$/wk at risk',
        width: 110,
        type: 'number',
        valueFormatter: ({ value }) => money(value == null ? null : Number(value)),
      },
      {
        field: 'actions',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<unknown, LossRow>) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              title="Who is gaining here?"
              onClick={() => setSelected({ row: p.row })}
            >
              <GroupsIcon fontSize="small" />
            </IconButton>
            <Link
              href={googleAdsUrl(p.row, days)}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 12, alignSelf: 'center' }}
            >
              GAds
            </Link>
          </Stack>
        ),
      },
    );
    return base;
  }, [level, days]);

  const rows = useMemo(
    () =>
      (losses?.rows ?? []).map((row, i) => ({
        id: `${row.campaign_id}-${row.ad_group_id ?? 'c'}-${i}`,
        ...row,
      })),
    [losses],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Auction Intelligence
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<NotificationsIcon />}
          onClick={() => setAlertSettingsOpen(true)}
        >
          Alert settings
        </Button>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
        >
          Upload auction insights
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Impression-share data through{' '}
        <b>{coverage?.impressionShare?.latest_date ?? '…'}</b>
        {' · '}competitor data (uploads) through{' '}
        <b>{coverage?.auctionInsights?.latest_week ?? 'none yet'}</b>
        {coverage?.auctionInsights?.domains
          ? ` (${coverage.auctionInsights.domains} domains)`
          : ''}
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Campaign health" />
        <Tab label="Competitor movers" />
        <Tab label="Our share losses" />
      </Tabs>

      {tab === 0 && <CampaignHealthTab service={service} />}

      {tab === 1 && <MoversTab service={service} />}

      {tab === 2 && (
        <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={level}
          onChange={(_e, v) => v && setLevel(v)}
        >
          <ToggleButton value="ad_group">Ad groups</ToggleButton>
          <ToggleButton value="campaign">Campaigns</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          size="small"
          label="Window"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          sx={{ width: 140 }}
        >
          <MenuItem value={7}>Last 7 days</MenuItem>
          <MenuItem value={14}>Last 14 days</MenuItem>
          <MenuItem value={28}>Last 28 days</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Min spend"
          value={minSpend}
          onChange={e => setMinSpend(Number(e.target.value))}
          sx={{ width: 130 }}
        >
          <MenuItem value={0}>$0</MenuItem>
          <MenuItem value={100}>$100</MenuItem>
          <MenuItem value={500}>$500</MenuItem>
          <MenuItem value={1000}>$1,000</MenuItem>
        </TextField>
        <IconButton onClick={refreshLosses} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Box sx={{ height: 640 }}>
        <StripedDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          disableSelectionOnClick
          getRowClassName={params =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          initialState={{
            sorting: {
              sortModel: [{ field: 'estimated_weekly_at_risk', sort: 'desc' }],
            },
          }}
        />
      </Box>
        </>
      )}

      <AlertSettingsDialog
        open={alertSettingsOpen}
        onClose={() => setAlertSettingsOpen(false)}
        onSaved={() => setAlertSettingsSaved(true)}
      />
      <Snackbar
        open={alertSettingsSaved}
        autoHideDuration={5000}
        onClose={() => setAlertSettingsSaved(false)}
      >
        <Alert severity="success" onClose={() => setAlertSettingsSaved(false)}>
          Alert settings saved — they apply from the next digest run.
        </Alert>
      </Snackbar>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={refreshCoverage}
        service={service}
      />

      <Dialog
        open={selected != null}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Competitors — {selected?.row.campaign_name}
          {selected?.row.ad_group_name ? ` › ${selected.row.ad_group_name}` : ''}
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selected && (
            <CompetitorPanel
              service={service}
              campaignId={String(selected.row.campaign_id)}
              adGroupId={
                selected.row.ad_group_id != null
                  ? String(selected.row.ad_group_id)
                  : undefined
              }
              title="Share by domain (uploaded auction insights, last 12 weeks)"
              store={selected.row.store}
              serpKeywordSuggestion={selected.row.ad_group_name ?? undefined}
            />
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
}
